// Odoslanie rezervačnej požiadavky majiteľovi.
// Primárne cez EmailJS (config.emailJs); ak nie je nastavený,
// otvorí sa e-mailový klient zákazníka s predvyplnenou správou.

import config from '../config';
import { formatDateSk, daysLabelSk } from './dates';
import { formatEur } from './pricing';

/**
 * Riadky so zľavou/voucherom vrátane kódu, napr.
 * "Voucher LETO20: -20 €" a/alebo "Zľava VITAJ10 (10 %): -9 €".
 * Prázdne pole, ak sa nepoužil ani voucher, ani percentuálna zľava.
 */
function discountLines(quote, discountCode) {
  const codePart = discountCode ? `${discountCode} ` : '';
  const lines = [];
  if (quote.voucherAmount > 0) {
    lines.push(`Voucher ${codePart}: -${formatEur(quote.voucherAmount)}`);
  }
  if (quote.discountAmount > 0) {
    lines.push(`Zľava ${codePart}(${quote.discountPercent} %): -${formatEur(quote.discountAmount)}`);
  }
  return lines;
}

/** Poskladá čitateľné zhrnutie rezervácie pre e-mail. */
export function buildBookingMessage({ form, startKey, endKey, quote, discountCode, reservationNumber }) {
  const lines = [
    `NOVÁ REZERVAČNÁ POŽIADAVKA – ${config.vehicleName}`,
    `Číslo rezervácie: ${reservationNumber}`,
    '',
    `Meno: ${form.firstName} ${form.lastName}`,
    `E-mail: ${form.email}`,
    `Telefón: ${form.phone}`,
    `Počet cestujúcich: ${form.travellers}`,
    '',
    `Vyzdvihnutie: ${formatDateSk(startKey)}`,
    `Vrátenie: ${formatDateSk(endKey)}`,
    `Dĺžka prenájmu: ${daysLabelSk(quote.daysCount)}`,
    '',
    'CENA:',
    ...quote.rateGroups.map(
      (group) => `  ${group.count} × ${formatEur(group.rate)}`
    ),
  ];

  if (quote.extras.length > 0) {
    lines.push('', 'DOPLNKY:');
    quote.extras.forEach((extra) => {
      lines.push(`  ${extra.label}: ${formatEur(extra.total)}`);
    });
  }

  const discount = discountLines(quote, discountCode);
  if (discount.length > 0) lines.push('', ...discount);

  lines.push(
    '',
    `CENA PRENÁJMU SPOLU: ${formatEur(quote.total)}`,
    `Vratná záloha: ${formatEur(quote.deposit)}`,
    '',
    `Správa od zákazníka:`,
    form.message || '(bez správy)'
  );

  return lines.join('\n');
}

export async function sendBookingRequest(payload) {
  const message = buildBookingMessage(payload);
  const { serviceId, templateId, publicKey } = config.emailJs;
  const { form, startKey, endKey, quote, discountCode, reservationNumber } = payload;

  if (serviceId && templateId && publicKey) {
    // Textový rozpis ceny pre e-mailovú šablónu (zobrazí sa po riadkoch).
    const priceLines = [
      ...quote.rateGroups.map((group) => `${group.count} × ${formatEur(group.rate)}`),
      ...quote.extras.map((extra) => `${extra.label}: ${formatEur(extra.total)}`),
      ...discountLines(quote, discountCode),
    ].join('\n');

    const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        service_id: serviceId,
        template_id: templateId,
        user_id: publicKey,
        // Premenné dostupné v šablóne EmailJS ako {{nazov}}
        template_params: {
          to_email: config.email,
          from_name: `${form.firstName} ${form.lastName}`,
          reply_to: form.email,
          reservation_number: reservationNumber,
          customer_name: `${form.firstName} ${form.lastName}`,
          customer_email: form.email,
          customer_phone: form.phone,
          travellers: form.travellers,
          pickup_date: formatDateSk(startKey),
          return_date: formatDateSk(endKey),
          duration: daysLabelSk(quote.daysCount),
          price_lines: priceLines,
          discount_code: discountCode || '',
          total: formatEur(quote.total),
          deposit: formatEur(quote.deposit),
          customer_message: form.message || '(bez správy)',
          message, // kompletné textové zhrnutie (záloha / auto-odpoveď)
        },
      }),
    });
    if (!response.ok) throw new Error('Odoslanie e-mailu zlyhalo');
    return { method: 'emailjs' };
  }

  // Záložné riešenie: mailto s predvyplnenou správou.
  // eslint-disable-next-line no-console
  console.warn(
    'EmailJS nie je plne nakonfigurovaný (serviceId/templateId/publicKey v src/config.js) – používam mailto fallback.',
    { serviceId: Boolean(serviceId), templateId: Boolean(templateId), publicKey: Boolean(publicKey) }
  );
  const subject = encodeURIComponent(
    `Rezervácia ${reservationNumber} – ${config.vehicleName}: ${formatDateSk(startKey)} – ${formatDateSk(endKey)}`
  );
  const body = encodeURIComponent(message);
  window.location.href = `mailto:${config.email}?subject=${subject}&body=${body}`;
  return { method: 'mailto' };
}
